/**
 * Estados globais reativos compartilhados entre componentes.
 *
 * Este arquivo e um esqueleto: declare aqui qualquer dado que precise
 * ser carregado uma unica vez por sessao (ex.: listas auxiliares como
 * categorias, unidades, tags, etc.) e use o helper `carregarRecurso`
 * para ter cache local + reload automatico apos TTL.
 *
 * Exemplo de uso em um componente:
 *   import { categoriasState, carregarDadosIniciais } from '@/state/globalData';
 *   onMounted(() => carregarDadosIniciais());
 *   // template:  v-for="c in categoriasState.data"
 */

import { reactive } from 'vue';
import { api } from '@/helpers/common/api';

const CACHE_PREFIX = 'global_data_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutos

const getCacheKey = (key) => `${CACHE_PREFIX}:${key}`;

const lerCache = (key, ttlMs) => {
  try {
    const bruto = localStorage.getItem(getCacheKey(key));
    if (!bruto) return null;
    const payload = JSON.parse(bruto);
    const savedAt = Number(payload?.savedAt || 0);
    if (!savedAt || (Date.now() - savedAt) > ttlMs) return null;
    return payload?.data ?? null;
  } catch (_error) {
    return null;
  }
};

const salvarCache = (key, data) => {
  try {
    localStorage.setItem(getCacheKey(key), JSON.stringify({ savedAt: Date.now(), data }));
  } catch (_error) { /* ignore */ }
};

/**
 * Carrega um recurso remoto e popula um state reativo.
 * Usa cache local com TTL para evitar refetch desnecessario.
 */
export const carregarRecurso = async ({ state, cacheKey, ttlMs = DEFAULT_TTL_MS, route }) => {
  state.error = null;
  const cacheValido = lerCache(cacheKey, ttlMs);
  if (cacheValido !== null) {
    state.data = cacheValido;
    state.loading = false;
    return cacheValido;
  }

  state.loading = true;
  try {
    const data = await api.read(route);
    if (data !== false && data !== null && data !== undefined) {
      state.data = data;
      salvarCache(cacheKey, data);
    }
    return state.data;
  } catch (error) {
    state.error = error;
    // eslint-disable-next-line no-console
    console.error(`Erro ao carregar ${cacheKey}:`, error);
    return state.data;
  } finally {
    state.loading = false;
  }
};

// ---------------------------------------------------------------------------
// Declare aqui os estados globais do seu projeto. Exemplos comentados:
// ---------------------------------------------------------------------------
// export const categoriasState = reactive({ data: [], loading: false, error: null });
// export const tagsState       = reactive({ data: [], loading: false, error: null });

// Mantenha um state minimo para o usuario autenticado (preenchido pelo login).
export const usuarioState = reactive({ data: null, loading: false, error: null });

/**
 * Limpa todos os caches globais. Chame no logout.
 */
export function limparDadosGlobais() {
  usuarioState.data = null;
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(`${CACHE_PREFIX}:`))
      .forEach((k) => localStorage.removeItem(k));
  } catch (_error) { /* ignore */ }
}

/**
 * Carrega todos os recursos globais que devem estar disponiveis logo
 * apos o login. Adicione aqui as chamadas conforme o seu projeto.
 *
 * Exemplo:
 *   await carregarRecurso({ state: categoriasState, cacheKey: 'categorias', route: '/categoria' });
 */
export async function carregarDadosIniciais() {
  const isLoggedIn = localStorage.getItem('userid');
  if (!isLoggedIn) return;

  // Adicione abaixo as cargas paralelas conforme necessario.
  await Promise.allSettled([
    // carregarRecurso({ state: categoriasState, cacheKey: 'categorias', route: '/categoria' }),
  ]);
}
